/**
 * Governance Service
 *
 * Story FOS-4.1.6: Proposal Submission & Verification
 * Task 7: Add governance canister client integration
 *
 * Client for governance canister operations including proposal creation.
 *
 * @see AC-4.1.6.4 - Clear error messages shown if submission fails
 * @see AC-4.1.6.5 - Successful submission shows confirmation with link
 *
 * @module services/governanceService
 */

import {
  // authz-001: the bearer-attaching wrapper (NOT @hello-world-dao/auth's
  // cookie-only authenticatedFetch) — oracle-bridge requireSessionAuth endpoints
  // (e.g. /summary) need the identity-service JWT bearer the cookie flow never sets.
  authenticatedFetchWithBearer as authenticatedFetch,
  getApiBaseUrl,
  createTimeout as createTimeoutBase,
  DEFAULT_TIMEOUT_MS,
  readErrorBody,
  selectServerMessage,
} from './apiHelpers';

// bl-1153-1: `selectServerMessage` moved to `apiHelpers.ts` so all five sibling
// mappers can reach it without importing this 1,200-line module. Re-exported here
// because `governanceService.voteErrors.test.ts` — bl-1153's regression evidence —
// imports it from this path and must keep passing unchanged.
export { selectServerMessage };

// =============================================================================
// Types
// =============================================================================

/**
 * Valid proposal categories matching governance canister enum.
 *
 * BL-189: kept in sync with governance/src/governance.did after GOV-FIX-001
 * (Immutable) and BL-086 (Personnel, Tier1Constitutional). All 7 variants
 * must be accepted here because the canister can return any of them on
 * existing proposals — narrowing to a 4-variant subset silently broke
 * category filtering for Personnel / Immutable / Tier1Constitutional.
 */
export type ProposalCategory =
  | 'Constitutional'
  | 'Personnel'
  | 'Operational'
  | 'Treasury'
  | 'SoftwareDevelopment'
  | 'Immutable'
  | 'Tier1Constitutional';

/**
 * Arguments for creating a new proposal
 * Maps to governance canister CreateProposalArgs
 */
export interface CreateProposalArgs {
  /** Proposal category */
  category: ProposalCategory | string;
  /** Title (1-200 characters) */
  title: string;
  /** Description (required, not empty) */
  description: string;
  /** Voting period (24-168 hours) */
  votingPeriodHours: number;
  /** Optional execution data (JSON string, max 100KB) */
  executionData?: string;
}

/**
 * Result of creating a proposal
 */
export interface CreateProposalResult {
  proposalId: number;
}

/**
 * Proposal status
 *
 * BL-326: Aligned with governance.did canister enum — oracle-bridge returns
 * the raw canister variant on `/api/governance/proposals/:id` (not the
 * dao-suite-mapped 4-state list status). The earlier 'Voting'/'Passed'/
 * 'Cancelled' triple never matched any real wire value.
 *
 * @see governance/src/governance.did (Draft | Active | Approved | Rejected | Executing | Executed | Failed | Invalid)
 */
export type ProposalStatus =
  | 'Draft'
  | 'Active'
  | 'Approved'
  | 'Rejected'
  | 'Executed'
  | 'Failed'
  // BL-450: retroactively invalidated via the §14.5 amended-proposal path.
  | 'Invalid';

/**
 * BL-189 / PLATFORM-004.1: A single entry in a proposal's edit history.
 *
 * When a proposal's description is updated via `update_proposal`, the PREVIOUS
 * description is appended to the proposal's `versions` list as a
 * `ProposalVersion` entry. The CURRENT description always lives on
 * `Proposal.description` (which is version `Proposal.versionNumber`, when
 * present).
 *
 * Timestamps are in nanoseconds since the Unix epoch to match the canister.
 */
export interface ProposalVersion {
  /** 1-indexed version number captured by this entry. */
  versionNumber: number;
  /** The proposal description text at this version. */
  content: string;
  /**
   * Nanoseconds-since-epoch timestamp when this version was superseded.
   *
   * BL-222: carried as a STRING, not a number. The canister stores this as a
   * `Nat64` (nanoseconds); realistic values (~1.75e18) exceed
   * `Number.MAX_SAFE_INTEGER` (~9.007e15), so parsing to `number` silently
   * loses precision. Kept as the raw digit string to match how the other
   * Nat64 wire timestamps (`created_at_ns`, `updated_at_ns`) are carried.
   */
  updatedAt: string;
  /** IC principal of the member that performed the update. */
  updatedBy: string;
}

/**
 * Proposal details
 *
 * BL-189: `versionNumber` + `versions` added to mirror governance.did after
 * PLATFORM-004.1. Both are optional because older proposals created before
 * the feature landed won't have them — consumers must treat `undefined` as
 * "original, never edited".
 */
export interface Proposal {
  id: number;
  category: ProposalCategory;
  title: string;
  description: string;
  votingPeriodHours: number;
  status: ProposalStatus;
  proposer: string;
  createdAt: number;
  executionData?: string;
  /** 1-indexed current version number (BL-189 / PLATFORM-004.1). */
  versionNumber?: number;
  /** Previous-description history, oldest first (BL-189 / PLATFORM-004.1). */
  versions?: ProposalVersion[];
}

/**
 * Vote choice type matching governance canister
 */
export type VoteChoice = 'Yes' | 'No' | 'Abstain' | 'NeedsRefinement';

/**
 * Request to cast a vote
 */
export interface CastVoteRequest {
  proposal_id: number;
  choice: VoteChoice;
  refinement_feedback?: string;
}

/**
 * Response from casting a vote
 */
export interface CastVoteResponse {
  success: boolean;
  vote_pass_nft_id?: number;
  error?: string;
}

/**
 * Vote breakdown for a proposal
 */
export interface VoteBreakdown {
  proposal_id: number;
  yes: number;
  no: number;
  abstain: number;
  needs_refinement: number;
  total_votes: number;
  quorum_threshold: number;
  quorum_met: boolean;
}

/**
 * Proposal list item status
 */
export type ProposalListStatus = 'Active' | 'Pending' | 'Passed' | 'Failed';

/**
 * Filters for proposal list.
 * BL-105 / BUG-DAO-017: Status values match oracle-bridge API contract (PascalCase).
 */
export interface ProposalFilters {
  /** Filter by status (all if not specified). Values match oracle-bridge VALID_DAO_SUITE_STATUSES. */
  status?: 'Active' | 'Pending' | 'Passed' | 'Failed' | 'all';
  /** Only show proposals user hasn't voted on yet */
  needsMyVote?: boolean;
}

/**
 * Proposal list item for browsing proposals
 *
 * BL-227: `_ns` timestamp strings added for sub-ms ordering precision.
 * Oracle-bridge emits these as 19-digit nanosecond-epoch strings (BL-222
 * dual-field timestamps). Ordering consumers should prefer these over the
 * ms fields; display consumers keep using the ms fields unchanged.
 * All `_ns` fields are optional — older oracle-bridge versions omit them.
 */
export interface ProposalListItem {
  id: number;
  title: string;
  category: string;
  status: ProposalListStatus;
  created_at: number;
  voting_ends_at: number;
  yes_votes: number;
  no_votes: number;
  abstain_votes: number;
  needs_refinement_votes: number;
  total_votes: number;
  quorum_threshold: number;
  user_has_voted: boolean;
  // BL-227: nanosecond-precision timestamps (optional — absent on older oracle-bridge).
  /** Nanoseconds-since-epoch string for creation time (BL-227). */
  created_at_ns?: string;
  /** Nanoseconds-since-epoch string for voting start time (BL-227). */
  voting_start_at_ns?: string;
  /** Nanoseconds-since-epoch string for voting end time (BL-227). */
  voting_ends_at_ns?: string;
}

/**
 * Vote history item for user's voting record
 *
 * BL-227: `voted_at_ns` added for sub-ms ordering of vote history entries.
 * Optional — absent on older oracle-bridge versions.
 */
export interface VoteHistoryItem {
  proposal_id: number;
  proposal_title: string;
  vote_choice: VoteChoice;
  vote_pass_nft_id: number;
  voted_at: number;
  /** Nanoseconds-since-epoch string for vote timestamp (BL-227). */
  voted_at_ns?: string;
}

/**
 * Result of checking if user has voted
 */
export interface HasVotedResult {
  hasVoted: boolean;
  voteChoice?: VoteChoice;
}

/**
 * Full proposal details for voting
 *
 * BL-189: `version_number` + `versions` mirror governance.did after
 * PLATFORM-004.1. Both optional because pre-feature proposals lack them.
 *
 * BL-227: `_ns` timestamp strings added for sub-ms ordering precision.
 * All `_ns` fields are optional — absent on older oracle-bridge versions.
 */
export interface ProposalDetails {
  id: number;
  category: ProposalCategory;
  title: string;
  description: string;
  summary: string;
  proposer: string;
  proposer_name: string;
  created_at: number;
  voting_ends_at: number;
  status: ProposalStatus;
  quorum_threshold: number;
  /** 1-indexed current version number (BL-189 / PLATFORM-004.1). */
  version_number?: number;
  /** Previous-description history, oldest first (BL-189 / PLATFORM-004.1). */
  versions?: ProposalVersion[];
  // BL-227: nanosecond-precision timestamps (optional — absent on older oracle-bridge).
  /** Nanoseconds-since-epoch string for creation time (BL-227). */
  created_at_ns?: string;
  /** Nanoseconds-since-epoch string for voting start time (BL-227). */
  voting_start_at_ns?: string;
  /** Nanoseconds-since-epoch string for voting end time (BL-227). */
  voting_ends_at_ns?: string;
  /** Nanoseconds-since-epoch string for last update time (BL-227). */
  updated_at_ns?: string;
}

/**
 * Governance error types
 */
export enum GovernanceErrorType {
  /** Input validation failed */
  VALIDATION_ERROR = 'validation_error',
  /** Not authorized to perform action */
  UNAUTHORIZED = 'unauthorized',
  /** Canister rejected the request */
  CANISTER_REJECTED = 'canister_rejected',
  /** Proposal already exists */
  DUPLICATE_PROPOSAL = 'duplicate_proposal',
  /** Resource not found */
  NOT_FOUND = 'not_found',
  /** Server error */
  SERVER_ERROR = 'server_error',
  /** Network error */
  NETWORK_ERROR = 'network_error',
  /** Request timeout */
  TIMEOUT = 'timeout',
  /** User not eligible to vote */
  NOT_ELIGIBLE = 'not_eligible',
  /** User already voted on this proposal */
  ALREADY_VOTED = 'already_voted',
  /** Voting period has ended */
  VOTING_CLOSED = 'voting_closed',
}

/**
 * Governance error with typed info
 */
export interface GovernanceError {
  type: GovernanceErrorType;
  message: string;
  existingProposalId?: number;
}

/**
 * Options for API calls
 */
export interface RequestOptions {
  /** Request timeout in milliseconds (default: 30000) */
  timeoutMs?: number;
}

// =============================================================================
// Constants
// =============================================================================

// BL-189: full 7-variant set matching governance.did after GOV-FIX-001 and
// BL-086. Previously only 4 variants were accepted, which silently rejected
// Personnel / Immutable / Tier1Constitutional proposals at the UI layer.
const VALID_CATEGORIES: ProposalCategory[] = [
  'Constitutional',
  'Personnel',
  'Operational',
  'Treasury',
  'SoftwareDevelopment',
  'Immutable',
  'Tier1Constitutional',
];

const MIN_TITLE_LENGTH = 1;
const MAX_TITLE_LENGTH = 200;
const MIN_VOTING_PERIOD_HOURS = 24;
const MAX_VOTING_PERIOD_HOURS = 168;
const MAX_FEEDBACK_LENGTH = 500;
// BL-190: canister enforces chars().count() ≤ 10,000 (unicode code-point count).
// Also enforced in updateProposal() — keep both in sync.
const MAX_DESCRIPTION_LENGTH = 10000;

// =============================================================================
// Helpers
// =============================================================================

/**
 * Create a timeout promise with GovernanceError type
 */
function createTimeout(ms: number): Promise<never> {
  return createTimeoutBase<GovernanceError>(ms, GovernanceErrorType.TIMEOUT);
}

// =============================================================================
// BL-227: Nanosecond timestamp helpers
// =============================================================================

/**
 * BL-227: Parse a raw nanosecond timestamp value from the wire.
 *
 * Oracle-bridge emits these as 19-digit strings (BL-222 dual-field
 * timestamps). Returns the string as-is when it looks like a valid
 * non-negative integer; returns undefined for null/undefined/invalid
 * values so callers can safely fall back to ms fields.
 */
export function parseNsTimestamp(raw: unknown): string | undefined {
  if (raw === null || raw === undefined) {
    return undefined;
  }
  const s = String(raw);
  // Accept only digit strings (no sign, no decimal — ns epochs are positive
  // integers too large to round-trip through JS number).
  if (/^\d+$/.test(s)) {
    return s;
  }
  return undefined;
}

/**
 * BL-227: Compare two ProposalListItem values by creation time using ns
 * precision when available, with ms-field fallback.
 *
 * Returns negative when a < b, zero when equal, positive when a > b.
 * Suitable for use with Array.sort().
 */
export function compareProposalsByCreatedAt(
  a: Pick<ProposalListItem, 'created_at' | 'created_at_ns'>,
  b: Pick<ProposalListItem, 'created_at' | 'created_at_ns'>
): number {
  // Both have ns fields: use BigInt comparison for full precision.
  if (a.created_at_ns !== undefined && b.created_at_ns !== undefined) {
    const aNs = BigInt(a.created_at_ns);
    const bNs = BigInt(b.created_at_ns);
    if (aNs < bNs) return -1;
    if (aNs > bNs) return 1;
    return 0;
  }
  // Fallback to ms field.
  return a.created_at - b.created_at;
}

/**
 * Validate proposal arguments
 * @throws GovernanceError if validation fails
 */
function validateCreateProposalArgs(args: CreateProposalArgs): void {
  // Validate category
  if (!VALID_CATEGORIES.includes(args.category as ProposalCategory)) {
    const error: GovernanceError = {
      type: GovernanceErrorType.VALIDATION_ERROR,
      message: `Invalid category: "${args.category}". Must be one of: ${VALID_CATEGORIES.join(', ')}`,
    };
    throw error;
  }

  // Validate title
  if (!args.title || args.title.length < MIN_TITLE_LENGTH) {
    const error: GovernanceError = {
      type: GovernanceErrorType.VALIDATION_ERROR,
      message: `Invalid title: must be at least ${MIN_TITLE_LENGTH} character(s)`,
    };
    throw error;
  }
  if (args.title.length > MAX_TITLE_LENGTH) {
    const error: GovernanceError = {
      type: GovernanceErrorType.VALIDATION_ERROR,
      message: `Invalid title: must be at most ${MAX_TITLE_LENGTH} characters`,
    };
    throw error;
  }

  // Validate description
  if (!args.description || args.description.trim().length === 0) {
    const error: GovernanceError = {
      type: GovernanceErrorType.VALIDATION_ERROR,
      message: 'Invalid description: must not be empty',
    };
    throw error;
  }
  // BL-190: use code-point count to match canister chars().count() behaviour.
  const descCharCount = Array.from(args.description).length;
  if (descCharCount > MAX_DESCRIPTION_LENGTH) {
    const error: GovernanceError = {
      type: GovernanceErrorType.VALIDATION_ERROR,
      message: `description exceeds maximum length of ${MAX_DESCRIPTION_LENGTH} chars`,
    };
    throw error;
  }

  // Validate voting period
  if (args.votingPeriodHours < MIN_VOTING_PERIOD_HOURS) {
    const error: GovernanceError = {
      type: GovernanceErrorType.VALIDATION_ERROR,
      message: `Invalid voting period: must be at least ${MIN_VOTING_PERIOD_HOURS} hours`,
    };
    throw error;
  }
  if (args.votingPeriodHours > MAX_VOTING_PERIOD_HOURS) {
    const error: GovernanceError = {
      type: GovernanceErrorType.VALIDATION_ERROR,
      message: `Invalid voting period: must be at most ${MAX_VOTING_PERIOD_HOURS} hours`,
    };
    throw error;
  }
}

/**
 * Map an HTTP status + ALREADY-PARSED body to a thrown GovernanceError.
 *
 * Takes parsed data rather than a `Response` precisely so it can be called after
 * the body has been consumed elsewhere (see `handleVoteErrorResponse`).
 *
 * The error TYPE per status is unchanged from the original mapping — only the
 * message improves. `VotingPage.tsx` branches on `type` for routing, so changing a
 * type here would silently become a navigation change there.
 *
 * @throws GovernanceError - always
 */
function throwMappedError(status: number, statusText: string, data: unknown): never {
  const serverMessage = selectServerMessage(data);

  if (status === 401 || status === 403) {
    const error: GovernanceError = {
      type: GovernanceErrorType.UNAUTHORIZED,
      message: serverMessage ?? 'Not authorized to perform this action',
    };
    throw error;
  }

  if (status === 404) {
    const error: GovernanceError = {
      type: GovernanceErrorType.NOT_FOUND,
      message: serverMessage ?? 'Resource not found',
    };
    throw error;
  }

  if (status === 409) {
    let existingId: number | undefined;
    if (typeof data === 'object' && data !== null) {
      const candidate = (data as Record<string, unknown>).existingProposalId;
      if (typeof candidate === 'number') existingId = candidate;
    }
    const error: GovernanceError = {
      type: GovernanceErrorType.DUPLICATE_PROPOSAL,
      message: serverMessage ?? 'A proposal with this content already exists',
      existingProposalId: existingId,
    };
    throw error;
  }

  if (status === 400 || status === 422) {
    const error: GovernanceError = {
      type: GovernanceErrorType.CANISTER_REJECTED,
      message: serverMessage ?? 'Request validation failed',
    };
    throw error;
  }

  if (status >= 500) {
    const error: GovernanceError = {
      type: GovernanceErrorType.SERVER_ERROR,
      message: serverMessage ?? `Server error: ${status} ${statusText}`,
    };
    throw error;
  }

  // Generic error
  const error: GovernanceError = {
    type: GovernanceErrorType.CANISTER_REJECTED,
    message: serverMessage ?? `Request failed: ${status} ${statusText}`,
  };
  throw error;
}

/**
 * Handle API error response
 * @throws GovernanceError
 */
async function handleErrorResponse(response: Response): Promise<never> {
  const data = await readErrorBody(response);
  return throwMappedError(response.status, response.statusText, data);
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Create a new proposal
 *
 * Submits a proposal to the governance canister for voting.
 *
 * @param args - Proposal creation arguments
 * @param options - Request options (timeout, etc.)
 * @returns Promise resolving to the created proposal ID
 * @throws GovernanceError on validation or submission failure
 */
export async function createProposal(
  args: CreateProposalArgs,
  options: RequestOptions = {}
): Promise<CreateProposalResult> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS } = options;

  // Validate inputs before making request
  validateCreateProposalArgs(args);

  const url = `${getApiBaseUrl()}/api/governance/proposals`;

  try {
    const fetchPromise = authenticatedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(args),
    });

    const response = await Promise.race([fetchPromise, createTimeout(timeoutMs)]);

    if (!response.ok) {
      await handleErrorResponse(response);
    }

    const data = await response.json();
    return { proposalId: data.proposalId };
  } catch (e) {
    // Re-throw GovernanceError
    if (isGovernanceError(e)) {
      throw e;
    }

    // Wrap other errors
    const error: GovernanceError = {
      type: GovernanceErrorType.NETWORK_ERROR,
      message: e instanceof Error ? e.message : 'Network error occurred',
    };
    throw error;
  }
}

/**
 * Get a proposal by ID
 *
 * @param proposalId - The proposal ID to retrieve
 * @param options - Request options
 * @returns Promise resolving to the proposal details
 * @throws GovernanceError on failure
 */
export async function getProposal(
  proposalId: number,
  options: RequestOptions = {}
): Promise<Proposal> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS } = options;

  const url = `${getApiBaseUrl()}/api/governance/proposals/${proposalId}`;

  try {
    const fetchPromise = authenticatedFetch(url, {
      method: 'GET',
    });

    const response = await Promise.race([fetchPromise, createTimeout(timeoutMs)]);

    if (!response.ok) {
      await handleErrorResponse(response);
    }

    const data = await response.json();
    return data as Proposal;
  } catch (e) {
    // Re-throw GovernanceError
    if (isGovernanceError(e)) {
      throw e;
    }

    // Wrap other errors
    const error: GovernanceError = {
      type: GovernanceErrorType.NETWORK_ERROR,
      message: e instanceof Error ? e.message : 'Network error occurred',
    };
    throw error;
  }
}

// =============================================================================
// Proposal Update API (BL-189 / PLATFORM-004.1)
// =============================================================================

/**
 * BL-189 / PLATFORM-004.1: Update the description of a Draft proposal.
 *
 * Mirrors governance canister `update_proposal(nat64, text, opt principal)`.
 * Only the original proposer can update, and only while the proposal is in
 * `Draft` status (the 24-hour review window). Each call appends the previous
 * description to the proposal's `versions` list; proposals are never deletable.
 *
 * Canister-side bounds (BL-190):
 *   - description max 10,000 characters (`chars().count()`, CJK-safe)
 *   - max 100 versions per proposal
 *
 * This is a placeholder REST wrapper — the backing oracle-bridge route lands in
 * PLATFORM-004.3. Until then, the fetch will hit a 404; callers should be
 * ready to handle NOT_FOUND / SERVER_ERROR until that story ships.
 *
 * @param proposalId - The proposal to update
 * @param newContent - The new description text (1..=10,000 chars)
 * @param options - Request options
 * @returns The new (current) version number on success
 * @throws GovernanceError on validation, authorization, or submission failure
 */
export async function updateProposal(
  proposalId: number,
  newContent: string,
  options: RequestOptions = {}
): Promise<{ versionNumber: number }> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS } = options;

  // Cheap client-side validation — the canister will repeat these checks.
  if (!newContent || newContent.trim().length === 0) {
    const error: GovernanceError = {
      type: GovernanceErrorType.VALIDATION_ERROR,
      message: 'Invalid description: must not be empty',
    };
    throw error;
  }

  // MAX_DESCRIPTION_LENGTH (BL-190). Use Array.from for proper unicode code-point
  // count that matches the canister's `chars().count()` behaviour.
  const charCount = Array.from(newContent).length;
  if (charCount > MAX_DESCRIPTION_LENGTH) {
    const error: GovernanceError = {
      type: GovernanceErrorType.VALIDATION_ERROR,
      message: `description exceeds maximum length of ${MAX_DESCRIPTION_LENGTH} chars`,
    };
    throw error;
  }

  const url = `${getApiBaseUrl()}/api/governance/proposals/${proposalId}`;

  try {
    const fetchPromise = authenticatedFetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ description: newContent }),
    });

    const response = await Promise.race([fetchPromise, createTimeout(timeoutMs)]);

    if (!response.ok) {
      await handleErrorResponse(response);
    }

    const data = await response.json();
    return { versionNumber: Number(data.versionNumber ?? data.version_number) };
  } catch (e) {
    if (isGovernanceError(e)) {
      throw e;
    }
    const error: GovernanceError = {
      type: GovernanceErrorType.NETWORK_ERROR,
      message: e instanceof Error ? e.message : 'Network error occurred',
    };
    throw error;
  }
}

/**
 * BL-189 / PLATFORM-004.1: Fetch the full previous-description history for a
 * proposal.
 *
 * Mirrors governance canister `get_proposal_versions(nat64)`. Returns every
 * PREVIOUS description of the proposal (oldest first). The CURRENT description
 * lives on `Proposal.description` — it is NOT included in this result.
 *
 * This is a placeholder REST wrapper — the backing oracle-bridge route lands in
 * PLATFORM-004.3.
 *
 * @param proposalId - The proposal whose history to fetch
 * @param options - Request options
 * @returns Ordered list of previous versions (oldest first); empty if never edited
 * @throws GovernanceError on failure
 */
export async function getProposalVersions(
  proposalId: number,
  options: RequestOptions = {}
): Promise<ProposalVersion[]> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS } = options;

  const url = `${getApiBaseUrl()}/api/governance/proposals/${proposalId}/versions`;

  try {
    const fetchPromise = authenticatedFetch(url, {
      method: 'GET',
    });

    const response = await Promise.race([fetchPromise, createTimeout(timeoutMs)]);

    if (!response.ok) {
      await handleErrorResponse(response);
    }

    const data = await response.json();
    const raw: unknown = data.versions ?? data;
    if (!Array.isArray(raw)) {
      return [];
    }

    // Accept both snake_case (canister wire format) and camelCase (backend DTO).
    return raw.map((entry: Record<string, unknown>) => ({
      versionNumber: Number(entry.versionNumber ?? entry.version_number ?? 0),
      content: String(entry.content ?? ''),
      // BL-222: keep the Nat64 nanosecond timestamp as a string — Number()
      // would round away precision for realistic ~1.75e18 values.
      updatedAt: String(entry.updatedAt ?? entry.updated_at ?? '0'),
      updatedBy: String(entry.updatedBy ?? entry.updated_by ?? ''),
    }));
  } catch (e) {
    if (isGovernanceError(e)) {
      throw e;
    }
    const error: GovernanceError = {
      type: GovernanceErrorType.NETWORK_ERROR,
      message: e instanceof Error ? e.message : 'Network error occurred',
    };
    throw error;
  }
}

// =============================================================================
// Vote Validation
// =============================================================================

/**
 * Validate cast vote request
 * @throws GovernanceError if validation fails
 */
function validateCastVoteRequest(request: CastVoteRequest): void {
  // Validate choice is valid
  const validChoices: VoteChoice[] = ['Yes', 'No', 'Abstain', 'NeedsRefinement'];
  if (!validChoices.includes(request.choice)) {
    const error: GovernanceError = {
      type: GovernanceErrorType.VALIDATION_ERROR,
      message: `Invalid vote choice: "${request.choice}". Must be one of: ${validChoices.join(', ')}`,
    };
    throw error;
  }

  // Validate NeedsRefinement requires feedback
  if (request.choice === 'NeedsRefinement') {
    if (!request.refinement_feedback || request.refinement_feedback.trim().length === 0) {
      const error: GovernanceError = {
        type: GovernanceErrorType.VALIDATION_ERROR,
        message: 'feedback is required when voting "Needs Refinement"',
      };
      throw error;
    }

    if (request.refinement_feedback.length > MAX_FEEDBACK_LENGTH) {
      const error: GovernanceError = {
        type: GovernanceErrorType.VALIDATION_ERROR,
        message: `Feedback exceeds maximum length of ${MAX_FEEDBACK_LENGTH} characters`,
      };
      throw error;
    }
  }
}

/**
 * Handle vote-specific error responses
 * @throws GovernanceError
 */
async function handleVoteErrorResponse(response: Response): Promise<never> {
  const status = response.status;

  // Read the body ONCE and keep the parsed value. bl-1153: this used to consume the
  // stream here and then hand the same (now-consumed) Response to handleErrorResponse,
  // whose own .json() rejected — stranding the hardcoded 'Request validation failed'
  // and naming the wrong subsystem to the member.
  const data = await readErrorBody(response);
  const errorCode =
    typeof data === 'object' && data !== null
      ? (data as Record<string, unknown>).error
      : undefined;

  if (status === 403 && errorCode === 'NOT_ELIGIBLE') {
    const error: GovernanceError = {
      type: GovernanceErrorType.NOT_ELIGIBLE,
      message: 'You are not eligible to vote on this proposal',
    };
    throw error;
  }

  if (status === 409 && errorCode === 'ALREADY_VOTED') {
    const error: GovernanceError = {
      type: GovernanceErrorType.ALREADY_VOTED,
      message: 'You have already voted on this proposal',
    };
    throw error;
  }

  if (status === 400 && errorCode === 'VOTING_CLOSED') {
    const error: GovernanceError = {
      type: GovernanceErrorType.VOTING_CLOSED,
      message: 'Voting has closed for this proposal',
    };
    throw error;
  }

  if (status === 404) {
    const error: GovernanceError = {
      type: GovernanceErrorType.NOT_FOUND,
      message: 'Proposal not found',
    };
    throw error;
  }

  // Fall back to the shared status mapping. The body was already consumed above, so
  // the PARSED data is handed down — never the consumed Response (bl-1153).
  return throwMappedError(status, response.statusText, data);
}

// =============================================================================
// Vote API Functions
// =============================================================================

/**
 * Cast a vote on a proposal
 *
 * @param request - Vote request with proposal ID and choice
 * @param options - Request options
 * @returns Promise resolving to vote result with NFT ID
 * @throws GovernanceError on validation or submission failure
 */
export async function castVote(
  request: CastVoteRequest,
  options: RequestOptions = {}
): Promise<CastVoteResponse> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS } = options;

  // Validate inputs before making request
  validateCastVoteRequest(request);

  const url = `${getApiBaseUrl()}/api/governance/proposals/${request.proposal_id}/vote`;

  try {
    const fetchPromise = authenticatedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    const response = await Promise.race([fetchPromise, createTimeout(timeoutMs)]);

    if (!response.ok) {
      await handleVoteErrorResponse(response);
    }

    const data = await response.json();
    return data as CastVoteResponse;
  } catch (e) {
    // Re-throw GovernanceError
    if (isGovernanceError(e)) {
      throw e;
    }

    // Wrap other errors
    const error: GovernanceError = {
      type: GovernanceErrorType.NETWORK_ERROR,
      message: e instanceof Error ? e.message : 'Network error occurred',
    };
    throw error;
  }
}

/**
 * Get vote breakdown for a proposal
 *
 * @param proposalId - The proposal ID
 * @param options - Request options
 * @returns Promise resolving to vote breakdown
 * @throws GovernanceError on failure
 */
export async function getVoteBreakdown(
  proposalId: number,
  options: RequestOptions = {}
): Promise<VoteBreakdown> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS } = options;

  const url = `${getApiBaseUrl()}/api/governance/proposals/${proposalId}/breakdown`;

  try {
    const fetchPromise = authenticatedFetch(url, {
      method: 'GET',
    });

    const response = await Promise.race([fetchPromise, createTimeout(timeoutMs)]);

    if (!response.ok) {
      await handleErrorResponse(response);
    }

    const data = await response.json();
    return data as VoteBreakdown;
  } catch (e) {
    // Re-throw GovernanceError
    if (isGovernanceError(e)) {
      throw e;
    }

    // Wrap other errors
    const error: GovernanceError = {
      type: GovernanceErrorType.NETWORK_ERROR,
      message: e instanceof Error ? e.message : 'Network error occurred',
    };
    throw error;
  }
}

/**
 * Get full proposal details for voting
 *
 * @param proposalId - The proposal ID
 * @param options - Request options
 * @returns Promise resolving to proposal details
 * @throws GovernanceError on failure
 */
export async function getProposalDetails(
  proposalId: number,
  options: RequestOptions = {}
): Promise<ProposalDetails> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS } = options;

  const url = `${getApiBaseUrl()}/api/governance/proposals/${proposalId}/details`;

  try {
    const fetchPromise = authenticatedFetch(url, {
      method: 'GET',
    });

    const response = await Promise.race([fetchPromise, createTimeout(timeoutMs)]);

    if (!response.ok) {
      await handleErrorResponse(response);
    }

    const data = await response.json();
    return data as ProposalDetails;
  } catch (e) {
    // Re-throw GovernanceError
    if (isGovernanceError(e)) {
      throw e;
    }

    // Wrap other errors
    const error: GovernanceError = {
      type: GovernanceErrorType.NETWORK_ERROR,
      message: e instanceof Error ? e.message : 'Network error occurred',
    };
    throw error;
  }
}

// =============================================================================
// Proposal List API Functions
// =============================================================================

/**
 * Get list of proposals with optional filters
 *
 * @param filters - Optional filters for status and needsMyVote
 * @param options - Request options
 * @returns Promise resolving to array of proposal list items
 * @throws GovernanceError on failure
 */
export async function getProposals(
  filters?: ProposalFilters,
  options: RequestOptions = {}
): Promise<ProposalListItem[]> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS } = options;

  // Build URL with query parameters
  let url = `${getApiBaseUrl()}/api/governance/proposals`;
  const params = new URLSearchParams();

  if (filters?.status && filters.status !== 'all') {
    params.append('status', filters.status);
  }
  if (filters?.needsMyVote) {
    params.append('needsMyVote', 'true');
  }

  const queryString = params.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  try {
    const fetchPromise = authenticatedFetch(url, {
      method: 'GET',
    });

    const response = await Promise.race([fetchPromise, createTimeout(timeoutMs)]);

    if (!response.ok) {
      await handleErrorResponse(response);
    }

    const data = await response.json();
    return data.proposals as ProposalListItem[];
  } catch (e) {
    // Re-throw GovernanceError
    if (isGovernanceError(e)) {
      throw e;
    }

    // Wrap other errors
    const error: GovernanceError = {
      type: GovernanceErrorType.NETWORK_ERROR,
      message: e instanceof Error ? e.message : 'Network error occurred',
    };
    throw error;
  }
}

/**
 * Get user's vote history
 *
 * @param principal - IC principal to fetch vote history for
 * @param options - Request options
 * @returns Promise resolving to array of vote history items
 * @throws GovernanceError on failure
 */
export async function getMyVotes(
  principal: string,
  options: RequestOptions = {}
): Promise<VoteHistoryItem[]> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS } = options;

  // BL-1057: call the real vote-history read. The bare /votes/:principal route
  // returns voter STATS (no `votes` key); /history returns the member's actual
  // voting record. Reading `data.votes` off the stats route yielded `undefined`
  // → setVotes(undefined) → MyVotesHistory crashed on `votes.length`.
  const url = `${getApiBaseUrl()}/api/governance/votes/${principal}/history`;

  try {
    const fetchPromise = authenticatedFetch(url, {
      method: 'GET',
    });

    const response = await Promise.race([fetchPromise, createTimeout(timeoutMs)]);

    if (!response.ok) {
      await handleErrorResponse(response);
    }

    const data = await response.json();
    // BL-1057: NEVER return undefined. A malformed / empty payload maps to [].
    const rawVotes = Array.isArray(data?.votes) ? data.votes : [];
    return rawVotes.map(
      (v: Record<string, unknown>): VoteHistoryItem => ({
        proposal_id: Number(v.proposal_id),
        proposal_title: String(v.proposal_title ?? ''),
        vote_choice: v.vote_choice as VoteChoice,
        // Vote-pass NFT may be absent (null) — coalesce to 0 for display.
        vote_pass_nft_id: v.vote_pass_nft_id == null ? 0 : Number(v.vote_pass_nft_id),
        voted_at: Number(v.voted_at),
        voted_at_ns: v.voted_at_ns == null ? undefined : String(v.voted_at_ns),
      }),
    );
  } catch (e) {
    // Re-throw GovernanceError
    if (isGovernanceError(e)) {
      throw e;
    }

    // Wrap other errors
    const error: GovernanceError = {
      type: GovernanceErrorType.NETWORK_ERROR,
      message: e instanceof Error ? e.message : 'Network error occurred',
    };
    throw error;
  }
}

/**
 * Check if user has voted on a specific proposal
 *
 * @param proposalId - Proposal ID to check
 * @param principal - IC principal to check
 * @param options - Request options
 * @returns Promise resolving to vote status
 * @throws GovernanceError on failure
 */
export async function hasUserVoted(
  proposalId: number,
  principal: string,
  options: RequestOptions = {}
): Promise<HasVotedResult> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS } = options;

  const url = `${getApiBaseUrl()}/api/governance/proposals/${proposalId}/voted/${principal}`;

  try {
    const fetchPromise = authenticatedFetch(url, {
      method: 'GET',
    });

    const response = await Promise.race([fetchPromise, createTimeout(timeoutMs)]);

    if (!response.ok) {
      await handleErrorResponse(response);
    }

    const data = await response.json();
    return data as HasVotedResult;
  } catch (e) {
    // Re-throw GovernanceError
    if (isGovernanceError(e)) {
      throw e;
    }

    // Wrap other errors
    const error: GovernanceError = {
      type: GovernanceErrorType.NETWORK_ERROR,
      message: e instanceof Error ? e.message : 'Network error occurred',
    };
    throw error;
  }
}

// =============================================================================
// Type Guard
// =============================================================================

/**
 * Type guard for GovernanceError
 */
export function isGovernanceError(error: unknown): error is GovernanceError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'type' in error &&
    'message' in error &&
    Object.values(GovernanceErrorType).includes((error as GovernanceError).type)
  );
}

// =============================================================================
// AI Summary (BL-045.2)
// =============================================================================

/**
 * Result of fetching an AI-generated proposal summary
 */
export interface ProposalAiSummaryResult {
  summary: string | null;
}

/**
 * Fetch an AI-generated summary for a governance proposal.
 *
 * Story BL-045.2: Governance Proposal Summarization
 *
 * Never throws — returns { summary: null } on any error (network, auth, etc.).
 * This ensures the AI summary never blocks page rendering.
 *
 * @param proposalId - The proposal ID to summarize
 * @returns Promise resolving to { summary: string | null }
 */
export async function getProposalAiSummary(
  proposalId: number,
): Promise<ProposalAiSummaryResult> {
  const url = `${getApiBaseUrl()}/api/governance/proposals/${proposalId}/summary`;
  try {
    const response = await authenticatedFetch(url, {
      method: 'GET',
    });
    if (!response.ok) {
      return { summary: null };
    }
    const data = await response.json();
    return { summary: data.summary ?? null };
  } catch {
    return { summary: null };
  }
}
